import { useTranslation } from "react-i18next";
import { calculateWeightPercentile } from "../shared/lib/insights";
import { displayVolume } from "../shared/lib/format";
import type { BabyProfile, DailyStats, DevelopmentalNorm, LogEvent, UnitSystem } from "../shared/types/domain";

interface ReferenceModalProps {
  kind: "feeding" | "diapers" | "growth" | "sleepPrediction";
  activeBaby: BabyProfile;
  norm: DevelopmentalNorm;
  logs: LogEvent[];
  todaysStats: DailyStats;
  effectiveFeedingMinMl: number;
  effectiveFeedingTargetMl: number;
  growthVelocity: number | null;
  ageWeeks: number;
  unitSystem: UnitSystem;
  onClose: () => void;
}

export function ReferenceModal({
  kind, activeBaby, norm, logs, todaysStats,
  effectiveFeedingMinMl, effectiveFeedingTargetMl, growthVelocity, ageWeeks, unitSystem, onClose,
}: ReferenceModalProps) {
  const { t } = useTranslation();

  const feedingMode = activeBaby.feedingMode ?? "bottle";
  const diaperTotal = todaysStats.diaperCountWet + todaysStats.diaperCountSoiled;
  const feedPct = effectiveFeedingTargetMl > 0 ? Math.round(((todaysStats.feedTotalMl - effectiveFeedingTargetMl) / effectiveFeedingTargetMl) * 100) : 0;
  const growthInRange = growthVelocity !== null && growthVelocity >= norm.growthMinGPerWeek && growthVelocity <= norm.growthMaxGPerWeek;
  const latestWeightLog = logs
    .filter((l) => l.type === "weight" && l.payload.weightG)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())[0];
  const latestWeightG = latestWeightLog?.payload.weightG;
  const latestWeightKg = latestWeightG !== undefined ? latestWeightG / 1000 : null;
  const percentileLabel = latestWeightG && activeBaby.sex
    ? calculateWeightPercentile(latestWeightG, ageWeeks, activeBaby.sex)
    : null;

  let title = "";
  let body: React.JSX.Element | null = null;
  let source = "";

  if (kind === "feeding") {
    title = t("modals.feedingTargetTitle");
    const isNursing = feedingMode === "nursing" || feedingMode === "both";
    body = latestWeightKg === null ? (
      <>
        <p className="ref-modal-body">{t("modals.feedingTargetFormula")}</p>
        <p className="ref-modal-body ref-modal-note">{t("modals.feedingTargetLogWeight")}</p>
      </>
    ) : (
      <>
        <p className="ref-modal-body">
          {t("modals.feedingTargetAt", {
            weight: unitSystem === "imperial" ? `${(latestWeightKg * 2.20462).toFixed(2)} lb` : `${latestWeightKg.toFixed(2)} kg`,
            min: displayVolume(effectiveFeedingMinMl, unitSystem),
            max: displayVolume(effectiveFeedingTargetMl, unitSystem),
            formula: unitSystem === "imperial" ? "2.3–2.7 oz/lb" : "150–180 ml/kg",
          })}
        </p>
        {!isNursing && (
          <p className="ref-modal-body">
            {t("modals.feedingTargetToday", { amount: displayVolume(todaysStats.feedTotalMl, unitSystem) })} —{" "}
            <span style={{ color: feedPct >= 0 ? "#16a34a" : feedPct >= -20 ? "#b45309" : "#dc2626", fontWeight: 600 }}>
              {feedPct >= 0 ? t("modals.feedingWithinTarget") : t("modals.feedingBelowTarget", { pct: Math.abs(feedPct) })}
            </span>.
          </p>
        )}
        {isNursing && <p className="ref-modal-body ref-modal-note">{t("modals.feedingNursingNote")}</p>}
      </>
    );
    source = t("modals.feedingSource");
  } else if (kind === "diapers") {
    title = t("modals.diapersTitle");
    const diaperPct = norm.diaperMin > 0 ? Math.round(((diaperTotal - norm.diaperMin) / norm.diaperMin) * 100) : 0;
    body = (
      <>
        <p className="ref-modal-body">{t("modals.diapersExpected", { weeks: ageWeeks, min: norm.diaperMin })}</p>
        <p className="ref-modal-body">
          {t("modals.diapersTodayCount", { count: diaperTotal })} —{" "}
          <span style={{ color: diaperPct >= 0 ? "#16a34a" : diaperPct >= -20 ? "#b45309" : "#dc2626", fontWeight: 600 }}>
            {diaperPct >= 0 ? t("modals.diapersWithin") : t("modals.diapersBelow", { pct: Math.abs(diaperPct) })}
          </span>.
        </p>
      </>
    );
    source = t("modals.diapersSource");
  } else if (kind === "growth") {
    title = t("modals.growthTitle");
    body = (
      <>
        <p className="ref-modal-body">{t("modals.growthExpected", { weeks: ageWeeks, min: norm.growthMinGPerWeek, max: norm.growthMaxGPerWeek })}</p>
        {growthVelocity !== null ? (
          <p className="ref-modal-body">
            {t("modals.growthVelocity", { velocity: growthVelocity })} —{" "}
            <span style={{ color: growthInRange ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
              {growthInRange ? t("modals.growthWithin") : t("modals.growthOutside")}
            </span>.
          </p>
        ) : (
          <p className="ref-modal-body ref-modal-note">{t("modals.growthLogTwo")}</p>
        )}
        {percentileLabel && <p className="ref-modal-body">{t("modals.growthPercentile", { label: percentileLabel })}</p>}
        {!activeBaby.sex && <p className="ref-modal-body ref-modal-note">{t("modals.growthSetSex")}</p>}
      </>
    );
    source = t("modals.growthSource");
  } else {
    title = t("modals.sleepPredictionTitle");
    body = (
      <>
        <p className="ref-modal-body">{t("modals.sleepPredictionMethod")}</p>
        <p className="ref-modal-body">{t("modals.sleepPredictionSplit")}</p>
        <p className="ref-modal-body ref-modal-note">{t("modals.sleepPredictionPrecision")}</p>
      </>
    );
    source = t("modals.sleepPredictionSource");
  }

  return (
    // Backdrop click-to-dismiss is a mouse/touch convenience; the "✕" button below
    // is the keyboard-accessible equivalent for closing this modal.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="ref-modal-overlay" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="ref-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ref-modal-header">
          <h3 className="ref-modal-title">{title}</h3>
          <button className="ref-modal-close" onClick={onClose}>✕</button>
        </div>
        {body}
        <p className="ref-modal-source">{source}</p>
      </div>
    </div>
  );
}
